import classNames from "classnames";
import clamp from "lodash/clamp";
import debounce from "lodash/debounce";
import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";

import css from "./movable_divider.module.scss";

export type MovableDividerProps = {
  ref?: React.Ref<HTMLDivElement>;
  qaId?: string;
  containerElement: HTMLDivElement | null;
  className?: string;
  style?: CSSProperties;
  initialLeftWidth?: number;
  minLeftWidth?: number;
  minRightWidth?: number;
  onResize?: (leftWidth: number) => void;
  onResizeStart?: (leftWidth: number) => void;
  onResizeFinished?: (leftWidth: number) => void;
  unit?: "px" | "percent";
};

export const MovableDivider = ({
  ref,
  qaId,
  containerElement,
  initialLeftWidth = 300,
  minLeftWidth = 100,
  minRightWidth = 100,
  onResize,
  onResizeStart,
  onResizeFinished,
  className,
  style,
  unit = "px"
}: MovableDividerProps) => {
  const [leftWidth, setLeftWidth] = useState<number>(initialLeftWidth);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(initialLeftWidth);

  const isUnitPx = unit === "px";

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      onResizeStart?.(leftWidth);
      startXRef.current = e.clientX;
      startWidthRef.current = leftWidth;
    },
    [leftWidth, onResizeStart]
  );

  const calculateNewLeftWidth = useCallback(
    (e: MouseEvent) => {
      const containerWidth = containerElement?.offsetWidth ?? 0;
      const deltaX = e.clientX - startXRef.current;
      const newLeftWidth = startWidthRef.current + (isUnitPx ? deltaX : (deltaX / containerWidth) * 100);

      if (isUnitPx) {
        const maxLeftWidth = containerWidth - minRightWidth;
        return clamp(newLeftWidth, minLeftWidth, maxLeftWidth);
      }

      return clamp(newLeftWidth, minLeftWidth, 100 - minRightWidth);
    },
    [containerElement, minLeftWidth, minRightWidth, isUnitPx]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerElement) {
        return;
      }

      const newLeftWidth = calculateNewLeftWidth(e);

      setLeftWidth(newLeftWidth);
      onResize?.(newLeftWidth);
    },
    [isDragging, calculateNewLeftWidth, onResize, containerElement]
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      setIsDragging(false);
      if (onResizeFinished) {
        const newLeftWidth = calculateNewLeftWidth(e);
        onResizeFinished(newLeftWidth);
      }
    },
    [calculateNewLeftWidth, onResizeFinished]
  );

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // make sure that divider is always visible
  useEffect(() => {
    if (!containerElement || !isUnitPx) {
      return;
    }

    const debouncedContainerRersizeHadler = debounce(() => {
      const containerWidth = containerElement.offsetWidth;

      if (containerWidth === 0) {
        return;
      }

      const newLeftWidth = clamp(leftWidth, minLeftWidth, containerWidth - minRightWidth);

      if (newLeftWidth === leftWidth) {
        return;
      }

      setLeftWidth(newLeftWidth);
      startWidthRef.current = newLeftWidth;
      onResize?.(newLeftWidth);
      onResizeFinished?.(newLeftWidth);
    }, 200);

    const resizeObserver = new ResizeObserver(() => {
      debouncedContainerRersizeHadler();
    });

    resizeObserver.observe(containerElement);

    return () => {
      resizeObserver.disconnect();
      debouncedContainerRersizeHadler.cancel();
    };
  }, [containerElement, leftWidth, minLeftWidth, minRightWidth, isUnitPx, onResize, onResizeFinished]);

  const maxLeftWidth = containerElement ? containerElement.offsetWidth - minRightWidth : 0;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={ref}
      data-qa-id={qaId}
      style={style}
      className={classNames(css.divider, className, {
        [css.dividerActive]: isDragging
      })}
      onMouseDown={handleMouseDown}
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={leftWidth}
      aria-valuemin={minLeftWidth}
      aria-valuemax={maxLeftWidth}
    >
      <div className={css.dividerHandle} />
    </div>
  );
};
